import {
    BoloPanelFormType,
    BoloPanelMetadata,
    EventPanelFormType,
    EventPanelMetadata,
    PhoneCallDto,
} from '@mark43/cad-api';
import { AttachmentsPanelType } from '../../../../attachments/types';
import { computed, makeObservable, observable } from 'mobx';
import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { WorkspacePanel } from '../../../workspace-panel';
import { ExpandablePanelSize } from '../../../expandable-panel-size';
import { useCallImport } from '../../../../event-panel/tabs/ani-ali-e911/use-call-import';

export type EventPanelData = {
    eventId?: number;
    form: UseFormReturn<EventPanelFormType>;
    phoneCall?: PhoneCallDto;
    workspacePanel: WorkspacePanel;
    metadata: EventPanelMetadata | undefined;
    clearEvent: () => void;
    generatePDF: () => void;
    generateRen: () => void;
    saveEvent: () => void;
    transmitEvent: () => void;
    setEventPanelSize: React.Dispatch<React.SetStateAction<ExpandablePanelSize>>;
    callImport: Record<keyof ReturnType<typeof useCallImport>, () => void>;
};

type HistoricalEventData = {
    agencyEventNumber?: string;
    cfsCode?: string;
    cfsDescription?: string;
    agencyTypeGlobalAttrId?: number;
    lastFocused: number;
    eventId: number;
};

export type BoloPanelData = {
    boloId?: number;
    form: UseFormReturn<BoloPanelFormType>;
    metadata: BoloPanelMetadata;
    workspacePanel: WorkspacePanel;
};

export type HistoricalBoloPanelData = {
    boloNumber?: string;
    title: string;
    boloId: number;
    lastFocused: number;
    boloType: string;
};

export type AttachmentsPanelData = {
    entityId?: number;
    entityType: AttachmentsPanelType;
    metadata: {
        lastUpdatedDateUtc: string;
        title: string;
        entityId: number;
        entityType: AttachmentsPanelType;
        entityNumber: string;
    };
    workspacePanel: WorkspacePanel;
};

export type HistoricalAttachmentsPanelData = {
    entityId: number;
    entityType: AttachmentsPanelType;
    entityNumber: string;
    lastFocused: number;
};

export type TrackedEntity = EventPanelData | BoloPanelData | AttachmentsPanelData;
export type TrackedHistoricalEntity =
    | HistoricalEventData
    | HistoricalBoloPanelData
    | HistoricalAttachmentsPanelData;

export class EntityPanelManager {
    @observable
    public eventPanels: Record<string, EventPanelData> = {};
    @observable.deep
    public historicalEventPanels: Record<number, HistoricalEventData> = {};

    @observable
    public boloPanels: Record<string, BoloPanelData> = {};
    @observable.deep
    public historicalBoloPanels: Record<number, HistoricalBoloPanelData> = {};

    @observable
    public attachmentsPanels: Record<string, AttachmentsPanelData> = {};
    @observable.deep
    public historicalAttachmentsPanels: Record<number, HistoricalAttachmentsPanelData> = {};

    constructor() {
        makeObservable(this);
    }

    getEventPanel(uuid: string): EventPanelData | undefined {
        return this.eventPanels[uuid];
    }

    setEventPanel(uuid: string, eventPanelData: EventPanelData): void {
        this.eventPanels[uuid] = eventPanelData;
        const eventId = eventPanelData.metadata?.eventId;
        if (eventId) {
            this.historicalEventPanels[eventId] = {
                eventId,
                agencyEventNumber: eventPanelData.metadata?.agencyEventNumber,
                agencyTypeGlobalAttrId: eventPanelData.metadata?.agencyTypeGlobalAttrId,
                cfsCode: eventPanelData.metadata?.callForServiceCode,
                cfsDescription: eventPanelData.metadata?.callForServiceDescription,
                lastFocused: Date.now(),
            };
        }
    }

    removeEventPanel(uuid: string): void {
        delete this.eventPanels[uuid];
    }

    @computed
    get eventPanelsList(): EventPanelData[] {
        return Object.values(this.eventPanels);
    }

    @computed
    get historicalEventPanelsList(): HistoricalEventData[] {
        return Object.values(this.historicalEventPanels);
    }

    getAllEventPanels(): EventPanelData[] {
        return Object.values(this.eventPanels);
    }

    @computed
    get boloPanelList(): BoloPanelData[] {
        return Object.values(this.boloPanels);
    }

    @computed
    get historicalBoloPanelsList(): HistoricalBoloPanelData[] {
        return Object.values(this.historicalBoloPanels);
    }

    getAllBoloPanels(): BoloPanelData[] {
        return Object.values(this.boloPanels);
    }

    setBoloPanel(uuid: string, boloPanelData: BoloPanelData): void {
        this.boloPanels[uuid] = boloPanelData;
        const boloId = boloPanelData.boloId;
        if (boloId) {
            this.historicalBoloPanels[boloId] = {
                boloId,
                boloNumber: boloPanelData.metadata.boloNumber,
                lastFocused: Date.now(),
                boloType: boloPanelData.form.getValues('boloType.label'),
                title: boloPanelData.metadata.title,
            };
        }
    }

    removeBoloPanel(uuid: string): void {
        delete this.boloPanels[uuid];
    }

    getAttachmentsPanel(uuid: string): AttachmentsPanelData | undefined {
        return this.attachmentsPanels[uuid];
    }

    setAttachmentsPanel(uuid: string, attachmentsPanelData: AttachmentsPanelData): void {
        this.attachmentsPanels[uuid] = attachmentsPanelData;
        const entityId = attachmentsPanelData.entityId;
        if (entityId) {
            this.historicalAttachmentsPanels[entityId] = {
                entityId,
                entityType: attachmentsPanelData.entityType,
                entityNumber: attachmentsPanelData.metadata.entityNumber,
                lastFocused: Date.now(),
            };
        }
    }

    removeAttachmentsPanel(uuid: string): void {
        delete this.attachmentsPanels[uuid];
    }

    @computed
    get attachmentsPanelsList(): AttachmentsPanelData[] {
        return Object.values(this.attachmentsPanels);
    }

    @computed
    get historicalAttachmentsPanelsList(): HistoricalAttachmentsPanelData[] {
        return Object.values(this.historicalAttachmentsPanels);
    }

    getAllAttachmentsPanels(): AttachmentsPanelData[] {
        return Object.values(this.attachmentsPanels);
    }
}

const EntityPanelManagerContext = React.createContext<EntityPanelManager | undefined>(undefined);

export function EntityPanelManagerProvider(props: {
    children: React.ReactNode;
}): React.ReactElement {
    const eventPanelManager = React.useState(() => new EntityPanelManager())[0];
    return (
        <EntityPanelManagerContext.Provider value={eventPanelManager}>
            {props.children}
        </EntityPanelManagerContext.Provider>
    );
}

export function useEntityPanelManager(): EntityPanelManager {
    const value = React.useContext(EntityPanelManagerContext);
    if (!value) throw new Error('Unexpectedly did not find EntityPanelManager on context');
    return value;
}
